import type { Action } from 'components';
import { usePlanMeta, usePlanMutations } from '../state';
import { useAtomValue, useStore } from 'jotai';
import { importPropsAtom, planAtom } from '../state/plan';
import { promptExportPlanOptions } from './ExportPlanOptionsDialog';
import { cancelExport } from '../const';
import { toast } from 'react-toastify';
import { openModal } from '@ff-it/ui';
import { ImportBlockDialog } from './ImportBlockDialog';
import { ImportPlanDialog } from './ImportPlanDialog';

const exportActions = ['plan', 'reservation', 'legacy_rows'] as const;

export type ExportAction = (typeof exportActions)[number];

const importActions = ['block', 'plan'] as const;
export type ImportAction = (typeof importActions)[number];

export function useIOActions(): (Action | '-')[] {
  const { id, type, planning } = usePlanMeta();
  const { isNaive, hasLockedRows, hasRows } = useAtomValue(importPropsAtom);
  const store = useStore();
  const { exportHandler, importHandler } = usePlanMutations();

  const actions: (Action | '-')[] = [
    {
      action: 'export-plan',
      button: {
        children: 'Export plan',
        onClick: async () => {
          const planState = store.get(planAtom);
          const exportOptions = await promptExportPlanOptions(planState);
          if (exportOptions === cancelExport) {
            toast.warn('Export canceled');
            return;
          }
          await exportHandler('plan', exportOptions);
        },
      },
    },
    {
      action: 'export-legacy_rows',
      button: {
        children: 'Export legacy rows',
        onClick: async () => {
          exportHandler('legacy_rows');
        },
      },
    },
  ];

  if (!isNaive) {
    actions.push({
      action: 'export-reservation',
      button: {
        children: 'Export media reservation',
        onClick: async () => {
          exportHandler('reservation');
        },
      },
    });
  }

  if (planning) {
    actions.unshift('-');

    // appends rows to exsting plan
    actions.unshift({
      action: 'import-block',
      button: {
        children: 'Import block (code)',
        onClick: async () =>
          openModal((props) => (
            <ImportBlockDialog
              blockId={id}
              type={type}
              {...props}
              submitHandler={({ block }) => importHandler('block', { block: block.id })}
            />
          )),
      },
    });
    // import naive rows
    if (!hasLockedRows && isNaive) {
      // destructive
      actions.unshift({
        action: 'import-plan',
        button: {
          children: 'Import plan (XLSX)',
          onClick: async () => {
            // can't destroy linked rows (REPLAN)
            if (hasRows && !window.confirm('This is going to rewrite rows. All changes will be lost. Are you sure?')) {
              return;
            }
            openModal((props) => (
              <ImportPlanDialog
                {...props}
                submitHandler={(payload) => {
                  const fd = new FormData();
                  for (const [key, value] of Object.entries(payload)) {
                    if (value) {
                      fd.append(key, value as any);
                    }
                  }

                  return importHandler('plan', fd);
                }}
              />
            ));
          },
        },
      });
    }
  }

  return actions;
}
